import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fieldErrorsFrom, messageFrom, warnQuietly } from "@/lib/api";
import { trackQuoteCompleted } from "@/lib/analytics";
import { setPath } from "@/lib/paths";
import {
  DEFAULT_ANSWERS,
  buildReviewGroups,
  buildSteps,
  offersFinanceFunction,
  routedFallbackCopy,
  stepRoutesToFallback,
  validateStep,
} from "./schemas";
import { writeStored } from "./calculatorStorage";
import { GENERAL_FALLBACK, ROUTED_FALLBACK } from "./fallbackCopy";
import { useAutosave } from "./useAutosave";
import { useSessionBootstrap } from "./useSessionBootstrap";

/** Bring the first field they missed into view and put the cursor in it. */
function focusFirstError(fieldRefs, path) {
  const node = fieldRefs.current[path]?.current;
  if (!node) return;
  node.scrollIntoView({ behavior: "smooth", block: "center" });
  (node.querySelector?.("button, input") || node).focus?.();
}

/** What the forward button should say from where they are standing. */
function nextLabelFor({ step, stepIndex, reviewIndex, returnToReview }) {
  if (!step) return "Continue";
  if (returnToReview && step.kind === "questions") return "Save and back to review";
  if (stepIndex === reviewIndex - 1) return "Review my answers";
  if (step.kind === "review") return "Continue to my price";
  return "Continue";
}

/**
 * The whole calculator flow: which phase we are in, the answers so far, and
 * every move the customer can make.
 *
 * The page that uses this is only layout. Session bootstrap and autosave live
 * in their own hooks, and the wizard's own pieces (questions, review, contact
 * gate) are separate components — this is the one place that decides what
 * happens when a button is pressed.
 */
export function useCalculatorFlow() {
  const [phase, setPhase] = useState("choose"); // choose | wizard | routing | fallback | quote
  const [service, setService] = useState(null);
  const [answers, setAnswers] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [errors, setErrors] = useState({});
  const [focusPath, setFocusPath] = useState(null);
  const [returnToReview, setReturnToReview] = useState(false);

  const [busy, setBusy] = useState(false);
  const [gateErrors, setGateErrors] = useState(null);
  const [fallback, setFallback] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [welcomeBack, setWelcomeBack] = useState(null);

  // A larger bookkeeping enquiry is offered the finance function once. Saying
  // "I only need bookkeeping" is remembered, so the fork never comes back and
  // blocks them on their way through.
  const [financeOffer, setFinanceOffer] = useState(null);
  const bookkeepingOnly = useRef(false);

  const wizardRef = useRef(null);
  const fieldRefs = useRef({});
  const registerRef = useCallback((path, ref) => {
    fieldRefs.current[path] = ref;
  }, []);

  /** Drop straight back into the wizard with a session's saved answers. */
  const applySaved = useCallback((data) => {
    setService(data.service);
    setAnswers(data.answers || {});
    setStepIndex(data.step_index || 0);
    setPhase("wizard");
  }, []);

  const { sessionId, setSessionId, bootstrapping } = useSessionBootstrap({
    onResumed: applySaved,
    onOffer: setWelcomeBack,
  });

  const steps = useMemo(() => buildSteps(service, answers), [service, answers]);
  const safeIndex = Math.min(stepIndex, Math.max(0, steps.length - 1));
  const step = steps[safeIndex];
  const reviewIndex = steps.findIndex((s) => s.kind === "review");
  const reviewGroups = useMemo(
    () => (service ? buildReviewGroups(service, answers, steps) : []),
    [service, answers, steps],
  );

  useAutosave({
    sessionId,
    service,
    answers,
    stepIndex: safeIndex,
    phase,
    stepKey: step?.key || null,
    totalSteps: steps.length,
    atContactGate: step?.kind === "contact",
  });

  const scrollToWizard = useCallback(() => {
    setTimeout(() => {
      wizardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  const ensureSession = useCallback(
    async (nextService) => {
      if (sessionId) return sessionId;
      const data = await api.startSession({ service: nextService });
      setSessionId(data.session_id);
      writeStored({ session_id: data.session_id });
      return data.session_id;
    },
    [sessionId, setSessionId],
  );

  const goToFallback = (payload) => {
    setFallback(payload);
    setPhase("fallback");
    scrollToWizard();
  };

  const selectService = async (key) => {
    const opening = { ...(DEFAULT_ANSWERS[key] || {}) };
    setWelcomeBack(null);
    setErrors({});
    setFallback(null);
    setQuoteResult(null);
    setReturnToReview(false);
    setFinanceOffer(null);
    bookkeepingOnly.current = false;
    setService(key);
    setAnswers(opening);
    setStepIndex(0);
    setPhase("wizard");
    scrollToWizard();
    try {
      const id = await ensureSession(key);
      await api.saveSession(id, {
        service: key,
        answers: opening,
        step_index: 0,
        step_key: "start",
      });
    } catch (error) {
      // The customer can carry on even if the save is delayed.
      warnQuietly("could not save the opening answers", error);
    }
  };

  /**
   * One answer changes, plus anything that answer settles on the customer's
   * behalf. Choosing a broad transaction range, for instance, clears whatever
   * narrow range was picked under the previous one, so nothing stale is ever
   * priced.
   */
  const changeAnswer = (path, value, alsoSet = null) => {
    setAnswers((prev) => {
      let next = setPath(prev, path, value);
      if (alsoSet) {
        Object.entries(alsoSet).forEach(([key, val]) => {
          next = setPath(next, key, val);
        });
      }
      return next;
    });
    setErrors((prev) => {
      const touched = [path, ...Object.keys(alsoSet || {})];
      if (!touched.some((key) => prev[key])) return prev;
      const cleared = { ...prev };
      touched.forEach((key) => {
        cleared[key] = undefined;
      });
      return cleared;
    });
    setFocusPath(null);
  };

  /** True if the step is complete; otherwise flags and focuses what's missing. */
  const stepIsComplete = () => {
    if (step.kind !== "questions") return true;
    const found = validateStep(step, answers);
    if (!Object.keys(found).length) return true;
    const firstPath = Object.keys(found)[0];
    setErrors(found);
    setFocusPath(firstPath);
    focusFirstError(fieldRefs, firstPath);
    return false;
  };

  /** Ask the server what these answers price out at, if they price at all. */
  const evaluateNow = async (id) => {
    try {
      return await api.evaluate(id, { service, answers });
    } catch (error) {
      toast.error(messageFrom(error));
      return null;
    }
  };

  const goNext = async () => {
    if (!step || !stepIsComplete()) return;

    // The fork comes before anything is priced: where they have told us the
    // revenue is above what our standard pricing is built for, they choose the
    // service rather than being told no.
    if (!bookkeepingOnly.current && offersFinanceFunction(service, step, answers)) {
      setFinanceOffer({ revenueOver20m: true });
      setPhase("routing");
      scrollToWizard();
      return;
    }

    setBusy(true);
    try {
      const id = await ensureSession(service);
      await api
        .saveSession(id, {
          service,
          answers,
          step_index: safeIndex,
          step_key: step.key,
          total_steps: steps.length,
        })
        .catch((error) => {
          warnQuietly("could not save this step", error);
        });

      const routes = step.kind === "questions" && stepRoutesToFallback(step, answers);
      const routedCopy = routes ? routedFallbackCopy(step, answers) : null;
      const evaluation = await evaluateNow(id);
      if (evaluation?.outcome === "fallback") {
        goToFallback({
          headline: evaluation.fallback_headline,
          body: evaluation.fallback_body,
          reasons: evaluation.fallback_reasons || [],
        });
        return;
      }
      if (routes) {
        goToFallback(routedCopy || ROUTED_FALLBACK);
        return;
      }

      if (returnToReview && step.kind === "questions" && reviewIndex >= 0) {
        setReturnToReview(false);
        setStepIndex(reviewIndex);
      } else {
        setStepIndex(Math.min(safeIndex + 1, steps.length - 1));
      }
      setErrors({});
      setFocusPath(null);
      scrollToWizard();
    } finally {
      setBusy(false);
    }
  };

  /**
   * "I only need bookkeeping": remember it and carry straight on through the
   * normal calculator, exactly as if the fork had never appeared.
   */
  const continueWithBookkeeping = () => {
    bookkeepingOnly.current = true;
    setFinanceOffer(null);
    setPhase("wizard");
    void goNext();
  };

  /** Back to the revenue question to change the answer. */
  const reconsiderTurnover = () => {
    setFinanceOffer(null);
    setPhase("wizard");
    scrollToWizard();
  };

  const goBack = () => {
    setErrors({});
    setGateErrors(null);
    if (safeIndex === 0) {
      setPhase("choose");
      setService(null);
      return;
    }
    if (returnToReview && reviewIndex >= 0) {
      setReturnToReview(false);
      setStepIndex(reviewIndex);
      scrollToWizard();
      return;
    }
    setStepIndex(safeIndex - 1);
    scrollToWizard();
  };

  /** Jump back to a question from the review screen, then return to review. */
  const editAnswer = (targetIndex, path = null) => {
    setReturnToReview(true);
    setStepIndex(targetIndex);
    setFocusPath(path);
    setErrors({});
    scrollToWizard();
  };

  const submitGate = async (values) => {
    setBusy(true);
    setGateErrors(null);
    try {
      const id = await ensureSession(service);
      const data = await api.submitContactGate(id, { ...values, service, answers });
      if (data.outcome === "fallback") {
        goToFallback({
          headline: data.fallback_headline,
          body: data.fallback_body,
          reasons: data.fallback_reasons || [],
        });
        return;
      }
      setQuoteResult(data);
      setPhase("quote");
      trackQuoteCompleted(data);
      scrollToWizard();
    } catch (error) {
      const fieldErrors = fieldErrorsFrom(error);
      if (fieldErrors) setGateErrors(fieldErrors);
      else toast.error(messageFrom(error));
    } finally {
      setBusy(false);
    }
  };

  const resumeSaved = () => {
    if (!welcomeBack) return;
    applySaved(welcomeBack);
    setWelcomeBack(null);
    scrollToWizard();
  };

  const startOver = () => {
    writeStored(null);
    setSessionId(null);
    setWelcomeBack(null);
    setService(null);
    setAnswers({});
    setStepIndex(0);
    setErrors({});
    setFallback(null);
    setQuoteResult(null);
    setReturnToReview(false);
    setFinanceOffer(null);
    bookkeepingOnly.current = false;
    setPhase("choose");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openGeneralFallback = async () => {
    try {
      await ensureSession(service);
    } catch (error) {
      // The form still works without a session.
      warnQuietly("could not start a session for the fallback form", error);
    }
    goToFallback(GENERAL_FALLBACK);
  };

  const leaveFallback = () => {
    setFallback(null);
    setPhase(service ? "wizard" : "choose");
    scrollToWizard();
  };

  return {
    phase,
    service,
    answers,
    errors,
    focusPath,
    busy,
    gateErrors,
    fallback,
    financeOffer,
    quoteResult,
    welcomeBack,
    bootstrapping,
    sessionId,
    steps,
    step,
    stepIndex: safeIndex,
    reviewGroups,
    nextLabel: nextLabelFor({ step, stepIndex: safeIndex, reviewIndex, returnToReview }),
    wizardRef,
    registerRef,
    selectService,
    changeAnswer,
    goNext,
    goBack,
    continueWithBookkeeping,
    reconsiderTurnover,
    editAnswer,
    submitGate,
    resumeSaved,
    startOver,
    openGeneralFallback,
    leaveFallback,
  };
}
